var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, memo } from 'react';
import T from 'prop-types';
import { useFileSystemAccessDrawer } from 'app/domains/filesystem';
import { useACLCan } from 'app/domains/filesystem/hooks/useACLCan';
import { DocumentType, AclAction, EntryKind } from 'shared/domains/apollo/generated/types';
import DocumentTableRow from './DocumentTableRow';
import FolderTableRow from './FolderTableRow';
function FileSystemTableRow(props) {
    const { id, type, name, acl, teamAcls, onDuplicate, onRename, onMove, onQuickMove, onDelete } = props, restProps = __rest(props, ["id", "type", "name", "acl", "teamAcls", "onDuplicate", "onRename", "onMove", "onQuickMove", "onDelete"]);
    const { setEntryId } = useFileSystemAccessDrawer();
    const { accessLevel } = useACLCan({ userAcls: acl, teamAcls });
    const handleDuplicate = useCallback(() => {
        const { documentType } = restProps;
        const isFreeText = documentType === DocumentType.Freetext;
        onDuplicate({ type, id, isFreeText, name });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onDuplicate, type, id]);
    const handleRename = useCallback(() => {
        onRename({ type, id, name });
    }, [onRename, type, id, name]);
    const handleMove = useCallback(() => {
        onMove({ type, id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onMove, id]);
    const handleQuickMove = useCallback((source) => {
        const [sourceType, sourceId] = (source === null || source === void 0 ? void 0 : source.split(':')) || [];
        if (sourceType && sourceId) {
            onQuickMove({ type: sourceType, sourceId, targetId: id });
        }
    }, [onQuickMove, id]);
    const handleDelete = useCallback(() => {
        onDelete({ type, id });
    }, [onDelete, type, id]);
    const handleEditAccess = useCallback(() => {
        setEntryId(id);
    }, [id, setEntryId]);
    const newProps = {
        id,
        type,
        name,
        onRename: handleRename,
        onDelete: handleDelete,
        onEditAccess: handleEditAccess,
    };
    switch (type) {
        case EntryKind.Folder:
            return (<FolderTableRow {...restProps} {...newProps} accessLevel={accessLevel} onQuickMove={handleQuickMove} onMove={handleMove}/>);
        case EntryKind.Document:
            return (<DocumentTableRow {...restProps} {...newProps} accessLevel={accessLevel} onDuplicate={handleDuplicate} onMove={handleMove}/>);
        default:
            return null;
    }
}
FileSystemTableRow.propTypes = {
    id: T.string,
    type: T.oneOf([EntryKind.Folder, EntryKind.Document]).isRequired,
    name: T.string.isRequired,
    acl: T.arrayOf(T.shape({
        user: T.shape({
            id: T.string.isRequired,
            name: T.string,
            email: T.string,
        }),
        actions: T.arrayOf(T.oneOf([AclAction.Read, AclAction.Write, AclAction.Delete, AclAction.Traverse])),
    }).isRequired).isRequired,
    teamAcls: T.arrayOf(T.shape({
        id: T.string.isRequired,
        teamId: T.string.isRequired,
        actions: T.arrayOf(T.oneOf([AclAction.Read, AclAction.Write, AclAction.Delete, AclAction.Traverse])),
        team: T.shape({
            id: T.string.isRequired,
            name: T.string.isRequired,
        }),
    }).isRequired).isRequired,
    onDuplicate: T.func.isRequired,
    onRename: T.func.isRequired,
    onMove: T.func.isRequired,
    onQuickMove: T.func.isRequired,
    onDelete: T.func.isRequired,
};
FileSystemTableRow.defaultProps = {
    id: undefined,
};
FileSystemTableRow.displayName = 'FileSystemTableRow';
const MemoizedComponent = memo(FileSystemTableRow);
MemoizedComponent.displayName = 'FileSystemTableRow';
export default MemoizedComponent;
